// frontend/src/pages/PaychequeList/components/VoidConfirmModal.tsx
// Confirmation dialog for voiding a paycheque. Rendered only when `target` is set.

import React from "react";
import { RotateCcw } from "lucide-react";
import type { Paycheque } from "../types";
import { fmtDate } from "../helpers";
import { C, FONT } from "../styles";

interface VoidConfirmModalProps {
  target: Paycheque;
  reason: string;
  voiding: boolean;
  onReasonChange: (reason: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

export default function VoidConfirmModal({
  target,
  reason,
  voiding,
  onReasonChange,
  onCancel,
  onConfirm,
}: VoidConfirmModalProps): React.ReactElement {
  const confirmDisabled = voiding || !reason.trim();

  return (
    <div
      onClick={() => { if (!voiding) onCancel(); }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(18,38,43,0.5)",
        zIndex: 250,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.card,
          borderRadius: 14,
          padding: "24px 22px",
          width: 460,
          maxWidth: "94vw",
          boxShadow: "0 24px 60px rgba(18,26,43,0.28)",
          fontFamily: FONT,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: "50%", background: C.errBg, display: "grid", placeItems: "center" }}>
            <RotateCcw size={20} style={{ color: C.err }} />
          </div>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.ink }}>
            Void this paycheque?
          </h3>
        </div>

        <div style={{ fontSize: 13.5, color: C.ink, marginBottom: 16, lineHeight: 1.5 }}>
          This will mark {target.employee_name}'s paycheque for{" "}
          <strong>{fmtDate(target.pay_date)}</strong> as voided. This reverses their YTD totals and cannot be undone.
        </div>

        <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: C.ink, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 }}>
          Reason for voiding
        </label>
        <textarea
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          disabled={voiding}
          placeholder="e.g. Wrong hours entered, employee received duplicate payment..."
          rows={3}
          style={{
            width: "100%",
            padding: "10px 12px",
            border: "1.5px solid " + C.ink,
            borderRadius: 8,
            fontFamily: FONT,
            fontSize: 13,
            color: C.ink,
            resize: "vertical",
            boxSizing: "border-box",
            marginBottom: 16,
            outline: "none",
          }}
        />

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={onCancel}
            disabled={voiding}
            style={{
              padding: "10px 16px",
              background: C.card,
              border: "1.5px solid " + C.ink,
              borderRadius: 10,
              color: C.ink,
              fontSize: 14,
              fontWeight: 700,
              cursor: voiding ? "not-allowed" : "pointer",
              opacity: voiding ? 0.5 : 1,
              fontFamily: FONT,
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={confirmDisabled}
            style={{
              padding: "10px 16px",
              background: C.err,
              border: "none",
              borderRadius: 10,
              color: "#fff",
              fontSize: 14,
              fontWeight: 700,
              cursor: confirmDisabled ? "not-allowed" : "pointer",
              opacity: confirmDisabled ? 0.5 : 1,
              fontFamily: FONT,
            }}
          >
            {voiding ? "Voiding..." : "Void paycheque"}
          </button>
        </div>
      </div>
    </div>
  );
}